import React from 'react';

import map from 'lodash/map';
import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';
import includes from 'lodash/includes';

import { Box, Dialog, DialogContent, Fade, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { formatHistoryDateVerbose } from '../data/formatting';
import { NormalizedMatchPlayerData, PlainObject } from '../data_types/types';
import { courseSpans, formatToPar, resolveLayout, scorePlayer } from '../data/scorecard_insights';
import { placeLabel } from '../data/player_insights';
import { DS, Overline, formatSigned, numericSx } from './Dashboard';
import { ScoreMark, ScoreMarkLegend } from './ScoreMark';

interface Props {
    matchData: PlainObject | null;
    isOpen: boolean;
    onClose: () => void;
};

const cellSx = {
    px: 0.5,
    py: 0.75,
    textAlign: 'center' as const,
    fontSize: '13px',
    borderBottom: `1px solid ${DS.border}`,
    ...numericSx,
};

const MatchCardModal = ({ matchData, isOpen, onClose }: Props) => {
    if (isNil(matchData)) {
        return null;
    }

    const card: PlainObject | undefined = matchData.card;
    const holes = card ? resolveLayout(card.layout ?? []) : [];
    const spans = courseSpans(holes);
    const knownPar = holes.every(h => h.par !== null);
    const totalPar = holes.reduce((sum, h) => sum + (h.par ?? 0), 0);

    const players = sortBy(map(card?.playerData ?? [], (player: PlainObject) => {
        const { scores, strokes, toPar, retired } = scorePlayer(holes, player as NormalizedMatchPlayerData);
        return {
            pID: player.pID,
            name: player.name,
            scores,
            strokes,
            toPar,
            didWin: includes(card?.winners, player.pID),
            didRetire: retired,
        };
    }), p => [p.didRetire ? 1 : 0, p.strokes]);

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 250 }}
            PaperProps={{
                sx: {
                    background: DS.surface,
                    backgroundImage: 'none',
                    border: `1px solid ${DS.border}`,
                    borderRadius: DS.radius,
                    m: { xs: 1.5, sm: 4 },
                    width: { xs: 'calc(100% - 24px)', sm: undefined },
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                {/* Header */}
                <Box sx={{
                    px: { xs: 2, sm: 3 },
                    pt: { xs: 2, sm: 3 },
                    pb: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    borderBottom: `1px solid ${DS.border}`,
                }}>
                    <Box>
                        <Overline>Round #{matchData.matchNum} · {formatHistoryDateVerbose(matchData.date)}</Overline>
                        <Typography sx={{ fontSize: '22px', fontWeight: 800, color: DS.text, letterSpacing: '-0.02em' }}>
                            Scorecard
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap', fontSize: '13px', color: DS.textMuted, ...numericSx }}>
                            <span>Finished <strong style={{ color: DS.text }}>{placeLabel(String(matchData.position))}</strong></span>
                            <span>·</span>
                            <span>Rating <strong style={{ color: DS.text }}>{matchData.currentRating}</strong> ({formatSigned(matchData.ratingChange)})</span>
                            {spans.length > 0 && <><span>·</span><span>{map(spans, 'course').join(', ')}</span></>}
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} aria-label="Close" sx={{ color: DS.textMuted, '&:hover': { color: DS.text } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                    {!card ? (
                        <Typography sx={{ fontSize: '14px', color: DS.textMuted }}>
                            The scorecard for this round isn't available.
                        </Typography>
                    ) : (
                        <>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Box component="table" sx={{ width: '100%', minWidth: 120 + holes.length * 36 + 110, borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <Box component="th" />
                                            {spans.map((s, i) => (
                                                <Box key={i} component="th" colSpan={s.span} sx={{
                                                    ...cellSx,
                                                    fontSize: '12px',
                                                    fontWeight: 700,
                                                    color: DS.textMuted,
                                                    borderLeft: i > 0 ? `1px solid ${DS.borderStrong}` : 'none',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {s.course}
                                                </Box>
                                            ))}
                                            <Box component="th" colSpan={2} />
                                        </tr>
                                        <tr>
                                            <Box component="th" sx={{ ...cellSx, textAlign: 'left', color: DS.textFaint, fontWeight: 600 }}>Hole</Box>
                                            {holes.map((h, i) => (
                                                <Box key={i} component="th" sx={{ ...cellSx, color: DS.textFaint, fontWeight: 600 }}>{h.holeNum}</Box>
                                            ))}
                                            <Box component="th" sx={{ ...cellSx, color: DS.textFaint, fontWeight: 600 }}>Total</Box>
                                            <Box component="th" sx={{ ...cellSx, color: DS.textFaint, fontWeight: 600 }}>To par</Box>
                                        </tr>
                                        <tr>
                                            <Box component="th" sx={{ ...cellSx, textAlign: 'left', color: DS.textMuted, fontWeight: 600 }}>Par</Box>
                                            {holes.map((h, i) => (
                                                <Box key={i} component="td" sx={{ ...cellSx, color: DS.textMuted }}>{h.par ?? '–'}</Box>
                                            ))}
                                            <Box component="td" sx={{ ...cellSx, color: DS.textMuted }}>{knownPar ? totalPar : '–'}</Box>
                                            <Box component="td" sx={cellSx} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {players.map(p => (
                                            <tr key={p.pID ?? p.name}>
                                                <Box component="td" sx={{ ...cellSx, textAlign: 'left', whiteSpace: 'nowrap', pr: 2, fontWeight: 600, color: p.didRetire ? DS.loss : p.didWin ? DS.gain : DS.text }}>
                                                    {p.name}
                                                    {p.didWin && <Box component="span" sx={{ ml: 0.75, fontSize: '11px', color: DS.gold }}>Winner</Box>}
                                                    {p.didRetire && <Box component="span" sx={{ ml: 0.75, fontSize: '11px', color: DS.loss }}>Retired</Box>}
                                                </Box>
                                                {p.scores.map((s, i) => (
                                                    <Box key={i} component="td" sx={{ ...cellSx, p: 0.5 }}>
                                                        <ScoreMark cell={s} />
                                                    </Box>
                                                ))}
                                                <Box component="td" sx={{ ...cellSx, fontWeight: 700, color: DS.text }}>{p.strokes}</Box>
                                                <Box component="td" sx={{ ...cellSx, fontWeight: 700, color: p.toPar < 0 ? DS.gain : p.toPar > 0 ? DS.loss : DS.text }}>
                                                    {formatToPar(p.toPar)}
                                                </Box>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <ScoreMarkLegend />
                            </Box>
                            {!knownPar && (
                                <Typography sx={{ fontSize: '12px', color: DS.warning, mt: 1.5 }}>
                                    Some holes are on a course we don't have par data for yet, so to-par only counts the known holes.
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default MatchCardModal;
